<template>
  <div class="wg-form-section">
    <h2 class="wg-form-section__title mainColor--text">
      What new topics do you need content about?
    </h2>
    <p class="wg-form-section__subtitle">
      One topic per line
    </p>

    <div class="wg-form-section__textarea">
      <v-textarea
        outlined
        hide-details
        color="greenColor"
        v-model="keywordsText"
        auto-grow
        row-height="28"
        :rows="5"
        placeholder="Enter one topic per line"
        class="wg-form-section__textarea-input"
      />
    </div>

    <div class="wg-form-section__lang-loc">
      <v-autocomplete
        class="wg-form-section__select"
        outlined
        dense
        hide-details="auto"
        append-icon="keyboard_arrow_down"
        :items="availableLocations"
        item-text="location_name"
        item-value="location_code"
        v-model="selectedLocation"
        color="grayColor"
      />
      <v-autocomplete
        class="wg-form-section__select"
        outlined
        dense
        hide-details
        append-icon="keyboard_arrow_down"
        :items="availableLanguages"
        v-model="selectedLanguage"
        item-text="language_name"
        item-value="language_code"
        color="grayColor"
      />
    </div>

    <div class="wg-form-section__checkbox-wrap">
      <v-tooltip top color="transparent">
        <template v-slot:activator="{ on, attrs }">
          <div v-bind="attrs" v-on="on" class="wg-form-section__checkbox-activator">
            <v-checkbox
              class="wg-result-select-all-checkbox ma-0"
              color="success"
              off-icon="none"
              on-icon="check"
              hide-details
              :ripple="false"
              :input-value="!include_subdomains_in_analysis"
              @change="include_subdomains_in_analysis = !$event"
            >
              <template v-slot:label>
                <span style="color: var(--v-gray60Color-base); font-size: 0.9rem;">Do not include sub-domains in analysis</span>
              </template>
            </v-checkbox>
          </div>
        </template>
        <div class="white-bg-tooltip small-tooltip" style="color: var(--v-blackColor-base);">
          Only count rankings for URLs on your main site (example.com and www.example.com). Rankings on subdomains (like blog.example.com) will be ignored.
        </div>
      </v-tooltip>
    </div>

    <div class="wg-form-section__footer">
      <v-btn
        color="profileAnchorColor"
        class="wg-form-section__submit whiteColor--text"
        rounded
        :loading="submitting"
        :disabled="insightSubmitDisabled"
        @click="() => hasUnlimitedAccess?getDataAction():(() => {showCostModal = true})()"
      >
        <i class="material-icons wg-form-section__submit-icon">search</i>
        <span style="font-size: 0.9rem; font-weight: 700;">{{ submitButtonLabel }}</span>
      </v-btn>
      <span>
        <span style="padding:2px 6px; border: 1px solid var(--v-grayColor-base); border-radius: 18px; font-weight: normal;">{{ keywords.length }} / 100</span>
      </span>
    </div>

    <div
      v-if="!hasUnlimitedAccess"
      class="wg-form-section__credits d-flex align-center gap5"
    >
      <img v-if="$vuetify.theme.dark" src="/static/icon_credits_dark_mode.svg" alt="">
      <img v-else src="/static/icon_credits_light_mode.svg" alt="">
      <span>{{ get_keyword_research_metrics_price_for_num_of_keywords(keywords.length) }} POP Credit{{get_keyword_research_metrics_price_for_num_of_keywords(keywords.length) === 0 ?'s':''}}{{get_keyword_research_metrics_price_for_num_of_keywords(keywords.length) > 1 ?'s':''}}</span>

      <b-dropdown aria-role="list" position="is-bottom-right" hoverable>
        <template #trigger>
          <i class="material-icons wg-form-section__credits-info">info</i>
        </template>

        <b-dropdown-item custom style="width: 250px;">
          <div>
            <div class="mb-2 font-weight-bold">Pricing</div>
            <div v-for="[tier, price] in Object.entries(KEYWORD_RESEARCH_METRICS_PRICE_TIERS)" :key="tier" class="mb-1 d-flex align-center justify-space-between gap10">
              <span><strong>{{ tier }}</strong> Keywords</span> <strong style="width: 70px;">{{ price }} Credit{{ price > 1 ?'s':'' }}</strong>
            </div>
          </div>
        </b-dropdown-item>
      </b-dropdown>
    </div>

    <div class="modal keyword-data-cost-modal" :class="{'is-active': showCostModal}">
      <div class="modal-background" @click.stop="showCostModal = false"></div>
      <div class="modal-content">
        <div class="box modal-container">
          <div class="modal-content-root">
            <p class="title-font is-size-4 mainColor--text">Get keyword data</p>
            <div class="mainColor--text message-text">
              <div class="mb-2">You’ve selected <span class="font-weight-bold">{{ keywords.length }} keyword{{keywords.length >1 ?'s':''}}</span>.</div>
              <div class="mb-1">We’ll retrieve detailed metrics for {{keywords.length >1 ?'each':'the'}} keyword, including:</div>
              <ul class="mb-2" style="list-style: initial;">
                <li>Keyword Difficulty Score</li>
                <li>Search Volume</li>
                <li>Search Volume trend</li>
                <li>Ranking URL and Position</li>
                <li>Keyword Score</li>
                <li>SEO Competitiveness Index (SCI)</li>
                <li>AllinTitle</li>
                <li>KGR Score</li>
              </ul>

              This action will consume <span class="font-weight-bold">{{ get_keyword_research_metrics_price_for_num_of_keywords(keywords.length) }} POP Credit{{get_keyword_research_metrics_price_for_num_of_keywords(keywords.length) >1 ?'s':''}}</span>. Click <span class="font-weight-bold">Get Data</span> to proceed.
            </div>
            <div class="d-flex justify-end gap10 mt-5">
              <v-btn rounded outlined color="mainColor"
                class="text-none"
                @click.stop="() => {
                  showCostModal = false;
                }">
                <span>Cancel</span>
              </v-btn>
              <v-btn rounded color="profileAnchorColor"
                class="whiteColor--text text-none"
                :loading="submitting"
                @click.stop="getDataAction">
                <span>Get data</span>
              </v-btn>
            </div>
          </div>
        </div>
        <button
          class="custom-modal-close-btn"
          aria-label="close"
          @click="() => (showCostModal = false)">
          <i class="material-icons">close</i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { getHostName } from '@/utils';
import languages from '@/consts/dataforseo_languages.json';
import dataForSeoLocations from '@/consts/dataforseo_locations.json';
import {
  get_keyword_research_metrics_price_for_num_of_keywords,
  KEYWORD_RESEARCH_METRICS_PRICE_TIERS
} from '@/components/plans_constants.js';


const MAX_KEYWORDS_LIMIT = 100


export default {
  name: 'FormSectionWhiteGlove',
  props: {
    site: {},
    campaign: {},
    hasUnlimitedAccess: {},
    isWhiteGloveUser: {},
    isMissionControlSession: {},
    reloadUser: {},
    submitButtonLabel: {
      type: String,
      default: 'Get insights',
    },
    allowWhiteGloveSubmit: {
      type: Boolean,
      default: false,
    },
    afterSuccessNavigate: {
      type: Function,
      default: null,
    },
    isContentOnlyView: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      selectedLocation: null,
      selectedLanguage: null,
      include_subdomains_in_analysis: true,

      keywordsText: '',
      keywords: [],

      submitting: false,
      showCostModal: false,
    }
  },
  computed: {
    KEYWORD_RESEARCH_METRICS_PRICE_TIERS() {
      return KEYWORD_RESEARCH_METRICS_PRICE_TIERS
    },
    availableLocations () {
      return dataForSeoLocations.locations;
    },
    availableLanguages () {
      return languages;
    },
    canProceed () {
      if (this.submitting) {
        return false;
      }
      return (this.keywords?.length > 0 && Boolean(this.selectedLocation) && Boolean(this.selectedLanguage))
    },
    insightSubmitDisabled () {
      const wgBlocked = this.isWhiteGloveUser &&
        !this.allowWhiteGloveSubmit &&
        !(this.isMissionControlSession || this.isContentOnlyView)
      return !this.canProceed || wgBlocked
    },
  },
  watch: {
    keywordsText: {
      handler: function (newVal, oldVal) {
        if (newVal != oldVal) {
          // split by comma and line-break, and remove '?' and double spaces
          const cleanNewVal = newVal.replace(',', '\n').split('\n').reduce((acc, curVal) => {
            const keyword = curVal.replace('?', '').replace('.', '').replace(/\u200b/g, '').split(/\s+/).join(" ")
            acc.push(keyword)
            return acc
          }, []).join('\n')

          if (cleanNewVal !== newVal) {
            this.$nextTick(() => {
              this.keywordsText = cleanNewVal
            })
          } else {
            const keywords = this.keywordsText.split('\n').reduce((acc, curVal) => {
              if (curVal.trim()) {
                acc.push(curVal.trim())
              }
              return acc
            }, [])

            this.keywords = keywords.slice(0, MAX_KEYWORDS_LIMIT)
          }
        }
      },
      immediate: true
    }

  },
  methods: {
    get_keyword_research_metrics_price_for_num_of_keywords(num_of_keywords) {
      return get_keyword_research_metrics_price_for_num_of_keywords(num_of_keywords)
    },
    async getDataAction () {
      this.showCostModal = false;
      this.submitting = true

      try {
        let campaign = this.campaign
        if (!campaign || !campaign.id) {
          const loc = typeof this.selectedLocation === 'string'
            ? parseInt(this.selectedLocation, 10)
            : this.selectedLocation
          const cr = await this.$store.dispatch('createKeywordResearchCampaign', {
            campaignName: '',
            siteId: this.site.id,
            targetUrl: getHostName(this.site.baseURL),
            isDomainLevelCampaign: true,
            locationCode: loc,
            languageCode: this.selectedLanguage,
          })
          if (!(cr.data && cr.data.status === 'SUCCESS')) {
            this.$notify({
              group: 'info',
              type: 'error',
              text: (cr.data && cr.data.msg) ? cr.data.msg : 'Could not create keyword research campaign for this site.'
            })
            return
          }
          campaign = cr.data.campaign
          this.$emit('domain-campaign-created', campaign)
        }

        const data = {
          campaignId: campaign.id,
          keywords: this.keywords,
          domain: getHostName(campaign.targetUrl),
          locationCode: this.selectedLocation,
          languageCode: this.selectedLanguage,
          include_subdomains_in_analysis: this.include_subdomains_in_analysis,
        }
        const response = await this.$store.dispatch('createKeywordInsightResearch', data)
        if (response.data) {
          if (response.data.status == 'SUCCESS') {
            this.$store.commit('triggerAsyncTasksRefresh');
            this.reloadUser()
            const id = response.data.id
            if (typeof this.afterSuccessNavigate === 'function') {
              const loc = this.afterSuccessNavigate(id)
              if (loc) {
                this.$router.push(loc)
              }
            } else {
              this.$router.push({
                path: `/sites/${this.site.id}/campaign/${campaign.id}/keyword-insight/${id}`,
                ...(this.isContentOnlyView
                  ? { query: { fromQuickContentOrdersTab: '1' } }
                  : {}),
              })
            }
          } else if (response.data.status == 'FAILURE') {
            this.$notify({
              group: 'info',
              type: 'warning',
              text: response.data.msg || 'An error happened'
            })
          }
        }
      } finally {
        this.submitting = false
      }
    },
  },
  mounted () {
    if (this.$route.query.initLanguage) {
      this.selectedLanguage = this.$route.query.initLanguage
    } else if (this.campaign && this.campaign.languageCode) {
      this.selectedLanguage = this.campaign.languageCode
    } else {
      this.selectedLanguage = 'en'
    }

    if (this.$route.query.initLocation) {
      this.selectedLocation = this.$route.query.initLocation ? Number(this.$route.query.initLocation) : ''
    } else if (this.campaign && this.campaign.locationCode) {
      this.selectedLocation = Number(this.campaign.locationCode)
    } else {
      this.selectedLocation = 2840
    }
  }
}
</script>

<style lang="scss" scoped>
.wg-form-section {
  &__title {
    font-size: 1.1rem;
    font-weight: 700;
    line-height: 1.3;
    margin: 0 0 8px;
    letter-spacing: -0.02em;
  }

  &__subtitle {
    margin: 0 0 20px;
    font-size: 0.85rem;
    color: var(--v-gray2Color-base);
    line-height: 1.4;
  }

  &__textarea {
    margin-bottom: 16px;
  }

  &__textarea-input.v-textarea {
    ::v-deep {
      fieldset {
        border-color: var(--v-gray12Color-base);
        border-radius: 8px;
      }

      textarea {
        font-size: 0.95rem;
        margin-top: 12px !important;
        margin-bottom: 12px !important;
        line-height: 1.5 !important;
      }
    }
  }

  &__lang-loc {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 16px;

    @media (min-width: 600px) {
      flex-wrap: nowrap;
    }
  }

  &__select {
    flex: 1 1 100%;
    min-width: 0;

    @media (min-width: 600px) {
      flex: 1 1 0;
    }

    ::v-deep {
      .v-input__slot {
        border-radius: 8px !important;
        min-height: 44px !important;
      }

      input {
        font-size: 0.9rem;
        color: var(--v-blackColor-base) !important;
      }
    }
  }

  &__checkbox-wrap {
    margin-bottom: 24px;
  }

  &__checkbox-activator {
    display: inline-block;
  }

  &__footer {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px 16px;
    margin-bottom: 12px;
  }

  &__submit {
    text-transform: none !important;
    padding-left: 20px !important;
    padding-right: 24px !important;
    font-weight: 600;
    letter-spacing: 0.01em;
    height: 40px !important;

    ::v-deep .v-btn__content {
      gap: 8px;
    }
  }

  &__submit-icon {
    font-size: 22px !important;
  }

  &__credits {
    color: var(--v-gray2Color-base);
    margin-top: 4px;
  }

  &__credits-info {
    color: var(--v-gray9Color-base);
    cursor: pointer;
  }
}

::v-deep {
  .wg-result-select-all-checkbox {
    margin-top: 0;
    padding-top: 0;

    .v-input__slot {
      padding-left: 0 !important;
    }

    label {
      color: var(--v-mainColor-base);
      font-size: 0.85rem;
    }

    .v-input--selection-controls__input {
      border: 1px solid #999999 !important;
      border-radius: 4px;
      height: 20px !important;
      width: 20px !important;
    }

    &.v-input--is-label-active {
      .v-icon {
        font-size: 16px !important;
      }
    }
  }
}

.v-text-field {
      ::v-deep fieldset {
        border-color: var(--v-gray12Color-base);
      }
    }
</style>
